import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { C, F } from './theme'
import { Card, Btn, Icon } from './prims'
import { useForecast } from '../../hooks/useForecast'

// EM-10 — Commit handoff modal. Firing commit() promotes the Idea to a Draft
// event, seeds its Project Plan, and publishes the delivery window to The Big
// Plan · NYMR Action Calendar. The modal animates the server pipeline as five
// discrete steps so the handoff reads as a deliberate, legible ceremony rather
// than a spinner. Publish is the fragile stage (cross-service) and can be
// retried in isolation via commit({ publishOnly: true }).

type StepState = 'todo' | 'active' | 'done' | 'errored'

const STEP_LABELS = [
  'Creating Draft event in Events Manager',
  'Linking forecast, baseline & team',
  'Seeding the Project Plan from the delivery timeline',
  'Publishing to The Big Plan · NYMR Action Calendar',
  'Notifying stakeholder leads'
]

const PUBLISH_STEP = 3

function niceDates(dates: string[] | undefined): string {
  if (!dates || dates.length === 0) return 'the proposed dates'
  const fmt = dates.map((d) => {
    const dt = new Date(d)
    if (isNaN(dt.getTime())) return d
    return dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  })
  if (fmt.length === 1) return fmt[0]!
  if (fmt.length === 2) return `${fmt[0]} & ${fmt[1]}`
  return `${fmt.slice(0, -1).join(', ')} & ${fmt[fmt.length - 1]}`
}

export function CommitModal({ eventId, ev, onClose }: { eventId: number; ev: { id: number; name: string }; onClose: () => void }) {
  const { commit, fc } = useForecast(eventId)

  const [steps, setSteps] = useState<StepState[]>(['active', 'todo', 'todo', 'todo', 'todo'])
  const [done, setDone] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [actionUrl, setActionUrl] = useState<string | null>(null)

  const mounted = useRef(true)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Advance a visual step index 0→PUBLISH_STEP-1 on a ~600ms cadence so the
  // early stages read as real work even when the server answers quickly.
  function driveTo(limit: number) {
    if (tickRef.current) clearInterval(tickRef.current)
    let i = 0
    setSteps((prev) => {
      const next = [...prev]
      for (let s = 0; s < next.length; s++) next[s] = s === 0 ? 'active' : 'todo'
      return next
    })
    tickRef.current = setInterval(() => {
      if (!mounted.current) return
      i += 1
      if (i > limit) { if (tickRef.current) clearInterval(tickRef.current); return }
      setSteps((prev) => {
        const next = [...prev]
        for (let s = 0; s < i; s++) if (next[s] !== 'errored') next[s] = 'done'
        if (i <= limit) next[i] = 'active'
        return next
      })
    }, 600)
  }

  function finalise(res: { ok: boolean; published: boolean; publishError?: string | null; actionUrl?: string | null }) {
    if (tickRef.current) clearInterval(tickRef.current)
    if (!mounted.current) return
    setActionUrl(res.actionUrl ?? null)
    const next: StepState[] = res.ok
      ? ['done', 'done', 'done', res.published ? 'done' : 'errored', res.published ? 'done' : 'todo']
      : ['errored', 'todo', 'todo', 'todo', 'todo']
    setSteps(next)
    setDone(res.ok && res.published)
    setErrorMsg(res.ok ? (res.published ? null : res.publishError ?? 'Publish to the Action Calendar failed.') : 'Commit failed.')
  }

  // On mount: kick off the real commit and the visual timer together.
  useEffect(() => {
    mounted.current = true
    driveTo(PUBLISH_STEP)
    commit()
      .then((res) => finalise(res))
      .catch((err: unknown) => {
        if (tickRef.current) clearInterval(tickRef.current)
        if (!mounted.current) return
        const msg = err instanceof Error ? err.message : 'Something went wrong committing this forecast.'
        setSteps((prev) => {
          const next = [...prev]
          const active = next.findIndex((s) => s === 'active')
          const idx = active === -1 ? 0 : active
          next[idx] = 'errored'
          return next
        })
        setErrorMsg(msg)
      })
    return () => {
      mounted.current = false
      if (tickRef.current) clearInterval(tickRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function retryPublish() {
    if (!mounted.current) return
    setErrorMsg(null)
    setSteps((prev) => {
      const next = [...prev]
      next[PUBLISH_STEP] = 'active'
      next[PUBLISH_STEP + 1] = 'todo'
      return next
    })
    commit({ publishOnly: true })
      .then((res) => finalise(res))
      .catch((err: unknown) => {
        if (!mounted.current) return
        const msg = err instanceof Error ? err.message : 'Publish failed.'
        setSteps((prev) => { const next = [...prev]; next[PUBLISH_STEP] = 'errored'; return next })
        setErrorMsg(msg)
      })
  }

  const overlay = (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,50,42,0.45)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <style>{'@keyframes fc-commit-spin{to{transform:rotate(360deg)}}'}</style>
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540, width: '92%' }}>
      <Card style={{ padding: 26 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
          <span style={{ width: 40, height: 40, borderRadius: 11, background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="send" size={19} color={C.green} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: -0.3, lineHeight: 1.1 }}>Commit to plan</h2>
            <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, marginTop: 4 }}>Handing {ev.name} to the operating plan.</div>
          </div>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {STEP_LABELS.map((label, i) => {
            const st = steps[i]!
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px' }}>
                <StepIndicator state={st} />
                <span style={{
                  fontFamily: F.body,
                  fontSize: 13.5,
                  fontWeight: st === 'active' || st === 'errored' ? 700 : 600,
                  color: st === 'todo' ? C.inkFaint : st === 'errored' ? C.oxblood : st === 'done' ? C.ink : C.green
                }}>{label}</span>
              </div>
            )
          })}
        </div>

        {/* Publish-failed state → retry */}
        {steps[PUBLISH_STEP] === 'errored' && !done && (
          <div style={{ marginTop: 16, background: C.oxbloodSoft, border: `1px solid ${C.oxblood}33`, borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
              <Icon name="alert" size={16} color={C.oxblood} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 800, color: C.oxblood }}>Publish didn’t complete</div>
                <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.oxblood, opacity: 0.85, marginTop: 3 }}>
                  {errorMsg ?? 'The Draft event was created, but publishing to the Action Calendar failed.'}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Btn variant="danger" small icon="refresh" onClick={retryPublish}>Retry publish</Btn>
            </div>
          </div>
        )}

        {/* Hard failure (commit itself failed, step 0 errored) */}
        {steps[0] === 'errored' && (
          <div style={{ marginTop: 16, background: C.oxbloodSoft, border: `1px solid ${C.oxblood}33`, borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
              <Icon name="alert" size={16} color={C.oxblood} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 800, color: C.oxblood }}>Couldn’t commit</div>
                <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.oxblood, opacity: 0.85, marginTop: 3 }}>{errorMsg ?? 'Something went wrong. Nothing was changed.'}</div>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Btn variant="secondary" small onClick={onClose}>Close</Btn>
            </div>
          </div>
        )}

        {/* Success */}
        {done && (
          <div style={{ marginTop: 18 }}>
            <div style={{ background: C.greenBg, border: `1px solid ${C.greenSoft}`, borderRadius: 10, padding: 14, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <Icon name="checkc" size={18} color={C.green} />
              <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.green, lineHeight: 1.45 }}>
                Created as a Draft event and published to The Big Plan · NYMR Action Calendar for {niceDates(fc?.proposed.dates)}.
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={onClose}>Stay in Studio</Btn>
              {actionUrl && (
                <Btn variant="primary" icon="calendar" onClick={() => { window.open(actionUrl, '_blank', 'noopener') }}>Open Action Calendar</Btn>
              )}
            </div>
          </div>
        )}
      </Card>
      </div>
    </div>
  )

  return createPortal(overlay, document.body)
}

function StepIndicator({ state }: { state: StepState }) {
  if (state === 'active') {
    return (
      <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, animation: 'fc-commit-spin 0.9s linear infinite' }}>
        <Icon name="refresh" size={17} color={C.green} />
      </span>
    )
  }
  if (state === 'done') {
    return <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="checkc" size={18} color={C.green} /></span>
  }
  if (state === 'errored') {
    return <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="alert" size={17} color={C.oxblood} /></span>
  }
  // todo
  return (
    <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: C.line }} />
    </span>
  )
}
